<script lang="ts">
	import PageGrid16 from '../components/pageGrid16.svelte';
	import type { SbImage } from '../types';
	import Image from '../components/image.svelte';
	import SbRichtext from '../components/sbRichtext.svelte';
	import Button from '$lib/buttons/v3/button.svelte';
	import Close from '$lib/icons/close.svelte';
	import { fade } from 'svelte/transition';
	import { checkVisibility } from '$lib/actions/checkVisibility';
	import { onMount } from 'svelte';
	import type { ISbRichtext, ISbStoryData, SbBlokData } from '@storyblok/js';
	import { editable } from '../editable';
	import Blok from '../blok.svelte';

	interface Props {
		content: ISbStoryData<{
			headline: string;
			hero_asset: SbBlokData[];
			sticker: SbImage;
			subheading: string;
			inline_asset: SbBlokData[];
			text_content: ISbRichtext;
			expand_label: string;
			text_below: string;
			bg_color: string;
		}>['content'];
	}

	let { content }: Props = $props();

	let expanded = $state(false);

	let sectionElement: HTMLElement;
	let heroElement: HTMLElement;
	let contentHeight = $state(0);

	let boundaries = $state({ top: false, bottom: false });

	const bottomPadding = 16;

	let height = $state('max(50svh, 50vw)');

	function setHeight() {
		if (sectionElement) {
			contentHeight = sectionElement.scrollHeight;
		}

		if (heroElement) {
			height = `min(${heroElement.clientHeight + 32}px, max(70svh, 55vw))`;
		}
	}

	function checkScrollPosition() {
		if (!expanded || !sectionElement) return;

		const { top, bottom } = sectionElement.getBoundingClientRect();

		const maxHeight = Math.min(
			heroElement.clientHeight,
			Math.max(window.innerHeight * 0.5, window.innerWidth * 0.5)
		);

		boundaries = {
			top: top >= window.innerHeight - maxHeight,
			bottom: window.innerHeight >= bottom
		};
	}

	let btnShouldFloat = $derived(expanded && !boundaries.top && !boundaries.bottom);

	onMount(() => {
		setHeight();
	});
</script>

<svelte:window on:resize={setHeight} />

<section
	use:editable={content}
	use:checkVisibility={{ threshold: 0 }}
	onvisible={({ detail }) => {
		if (detail) {
			window.addEventListener('scroll', checkScrollPosition);
		} else {
			window.removeEventListener('scroll', checkScrollPosition);
		}
	}}
	bind:this={sectionElement}
	class="relative overflow-hidden py-16 text-white transition-height duration-600 ease-[cubic-bezier(0.22,1,0.36,1)] lg:pb-10"
	class:delay-300={!expanded}
	style="background-color: {content.bg_color || 'transparent'}; height: {expanded
		? `${contentHeight + bottomPadding}px`
		: height};"
>
	<div
		class="pointer-events-none absolute inset-x-0 top-0"
		style={`height: ${boundaries.bottom ? '100%' : height}`}
	>
		{#key expanded || boundaries.top || boundaries.bottom}
			<div
				in:fade={{ delay: 400, duration: 300 }}
				out:fade={{ duration: 0 }}
				class="bottom-0 z-10 flex w-full items-end justify-center pb-12 lg:pb-10"
				class:fixed={btnShouldFloat}
				class:absolute={!btnShouldFloat}
				style={expanded
					? null
					: 'background: linear-gradient(0deg, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0) 100%)'}
			>
				<Button
					class="btn-white pointer-events-auto"
					onclick={() => {
						expanded = !expanded;
						if (!expanded)
							scrollTo({
								top: sectionElement.offsetTop + heroElement.clientHeight - 50,
								behavior: 'smooth'
							});
					}}
				>
					<span class="flex items-center gap-x-3 px-3">
						{expanded ? 'Close' : content.expand_label}
						<Close
							class="-mr-2 h-6 w-6 transform-gpu transition-transform {expanded ? '' : 'rotate-45'}"
						/>
					</span>
				</Button>
			</div>
		{/key}
	</div>

	<PageGrid16 margin class="relative">
		<!-- svelte-ignore a11y_no_static_element_interactions -->
		<div class="col-span-full lg:col-start-3 lg:col-end-[15]" bind:this={heroElement}>
			<h1
				class="mx-auto text-balance text-center text-[36px]/[108%] tracking-[-.36px] max-lg:max-w-[56vw] lg:text-[64px]/[110%] lg:tracking-[-.64px]"
			>
				{content.headline}
			</h1>

			<!-- svelte-ignore a11y_click_events_have_key_events -->
			<div
				class="relative mt-10 aspect-square lg:mt-16 lg:aspect-[16/10]"
				onclick={() => (expanded = true)}
				class:cursor-pointer={!expanded}
			>
				{#if content.hero_asset?.length > 0}
					<Blok content={content.hero_asset[0]} />
				{/if}
				{#if content.sticker?.filename}
					<div class="absolute right-0 top-0 -translate-y-1/2 translate-x-4 lg:translate-x-1/2">
						<Image
							class="h-18 w-18 lg:h-25 lg:w-25"
							src={content.sticker.filename}
							alt={content.sticker.alt}
							title={content.sticker.title}
							height={150}
						/>
					</div>
				{/if}
			</div>
		</div>

		<div class="col-span-full lg:col-start-4 lg:col-end-10">
			<p
				class="mt-10 text-[24px]/[108%] tracking-[-.24px] lg:mt-23 lg:text-[32px]/[110%] lg:tracking-[-.32px]"
			>
				{content.subheading}
			</p>
		</div>

		<div
			class="col-span-full mt-[33px] aspect-3/4 overflow-hidden rounded-lg lg:col-start-3 lg:col-end-8 lg:mt-[85px]"
		>
			{#if content.inline_asset?.length > 0}
				<Blok content={content.inline_asset[0]} />
			{/if}
		</div>
		<div
			class="col-span-full mt-[33px] flex flex-col justify-center text-[16px]/[150%] tracking-[-.16px] lg:col-start-9 lg:col-end-[15] lg:mt-[85px] lg:text-[20px]/[140%] lg:tracking-[-.2px]"
		>
			<SbRichtext text={content.text_content} />
		</div>
		<p
			class="col-span-full mb-12 mt-14 text-[36px]/[112%] tracking-[-.36px] lg:col-start-4 lg:col-end-12 lg:mb-24 lg:mt-23 lg:text-[48px]/[110%] lg:tracking-[-.48px]"
		>
			{content.text_below}
		</p>
	</PageGrid16>
</section>

<style lang="scss">
	:global(p:has(.editorial-expand-question)) {
		margin-bottom: theme('spacing.3');
		color: #d5ad8a;
	}

	:global(p:has(.editorial-expand-answer):not(:last-of-type)) {
		margin-bottom: theme('spacing.7');

		@media (min-width: 1024px) {
			margin-bottom: theme('spacing.16');
		}
	}
</style>
